
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ChevronDownIcon from './icons/ChevronDownIcon.vue';
import CheckIcon from './icons/CheckIcon.vue';
import { debounce } from '@/vendor/pleased-sdk/util';

interface SelectorOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  components: {
    ChevronDownIcon,
    CheckIcon
  }
})
export default class SingleSelect extends Vue {
  @Prop({ type: Array, required: true }) readonly options!: SelectorOption[];
  @Prop({ type: [String, Number], default: '' }) readonly value!: string | number;
  @Prop({ type: String, default: 'Select an option' }) readonly placeholder!: string;
  @Prop({ type: String, default: 'Select' }) readonly ariaLabel!: string;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: String, default: 'above' }) readonly position!: 'above' | 'below';

  isOpen = false;
  selectedOption: SelectorOption | null = null;
  dropdownPosition = {
    top: '0px',
    left: '0px'
  };
  private debouncedUpdatePosition = debounce(this.updateDropdownPosition, 300);

  get selectedLabel(): string {
    if (this.selectedOption) {
      return this.selectedOption.label;
    }
    return '';
  }

  mounted() {
    this.updateDropdownPosition();
  }

  @Watch('value', { immediate: true })
  onValueChange(newValue: string | number) {
    this.selectedOption = this.options.find(option => option.value === newValue) || null;
  }

  @Watch('options', { immediate: true })
  onOptionsChange() {
    if (this.value) {
      this.selectedOption = this.options.find(option => option.value === this.value) || null;
    }
  }

  updateDropdownPosition() {
    const button = this.$refs.selectorButton as HTMLElement;
    const dropdown = this.$refs.dropdownMenu as HTMLElement;

    if (!button || !dropdown) return;

    const buttonRect = button.getBoundingClientRect();
    const gap = 10; // 10px gap between selector and dropdown

    // Get the actual dropdown height after it's rendered
    const dropdownHeight = dropdown.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top: number;
    let left = buttonRect.left;

    // Determine if dropdown should go above or below based on available space
    const shouldGoAbove =
      this.position === 'above' ||
      (this.position === 'below' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

    if (shouldGoAbove) {
      // Position above the button
      top = buttonRect.top - dropdownHeight - gap;
    } else {
      // Position below the button
      top = buttonRect.bottom + gap;
    }

    // Update dropdown position
    this.dropdownPosition = {
      top: `${top}px`,
      left: `${left}px`
    };
  }

  handleToggle() {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
    }
  }

  handleBlur(event: FocusEvent) {
    // Use a small delay to allow option clicks to register
    setTimeout(() => {
      if (!this.$el.contains(event.relatedTarget as Node)) {
        this.isOpen = false;
      }
    }, 100);
  }

  handleOptionSelect(option: SelectorOption) {
    if (option.disabled) return;

    this.selectedOption = option;
    this.isOpen = false;
    this.$emit('input', option.value);
    this.$emit('change', option.value);

    // Focus back to the button for accessibility
    (this.$refs.selectorButton as HTMLElement)?.focus();
  }

  isOptionSelected(option: SelectorOption) {
    return option.value === this.value;
  }
}
